import { useState, MouseEvent } from 'react';
import { PopulatedKlydo } from '../../../../api/klydo';
import { DSCardActions, DSCardContent } from '../../../../design-system/Card';
import DSBox from '../../../../design-system/DSBox';
import DSKlydoCard from '../../../../design-system/Klydo/DSKlydoCard';
import { useTheme } from '@mui/material';
import {
  DSTitleTypography,
  DSTypography,
} from '../../../../design-system/Typography';
import useKlydoCard from '../hooks/useKlydoCard';
import MenuButton from '../../../../components/MenuButton/MenuButton';
import KlydoStatus from './KlydoStatus';
import DSDivider from '../../../../design-system/DSDivider';
import {
  INCOMPLETE_PROFILE_ALERT,
  INCOMPLETE_PROFILE_REASON_ALERT,
  SUBMIT_FOR_REVIEW_ALERT,
  INCOMPLETE_KLYDO_DATA_ALERT,
  INCOMPLETE_KLYDO_DATA_REASON_ALERT,
} from '../../../../consts/alerts';
import ErrorDialog from '../../../../components/ErrorDialog';
import WarningDialog from '../../../../components/WarningDialog';
import SpecialPropertiesModal from '../../Publication/SpecialPropertiesModal';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DSRadio from '../../../../design-system/DSRadio';
import KlydoPreview from '../../../../components/KlydoPlayer/Wrappers/KlydoPreview';
import { PendulumLogoType } from '../../../../consts/pendulumLogo';

// Fits the widest status row ("Collection (Marketplace)" + icon), so the status
// text does not change the card width.
const STATUS_MIN_WIDTH = 184;

export type KlydoCardProps = {
  klydo: PopulatedKlydo;
  isStatic?: boolean;
  isMenuHidden?: boolean;
  isSelected?: boolean;
  isDraggedHovered?: boolean;
  showDragIconOnHover?: boolean;
  pendulumLogo?: string;
  pendulumLogoSize?: number;
  // See Pendulum.tsx: once true, logoUrl already arrives pre-sized and no
  // client-side scaling should be applied to it.
  isApprovedCollection?: boolean;
  // See resolveLogoDiameterPx: 'full' fills the whole ball regardless of
  // pendulumLogoSize, matching klydo-api's cover-crop bake for that type.
  pendulumLogoType?: PendulumLogoType;
  onSelect?: (klydo: PopulatedKlydo) => void;
  handleRemoveKlydoFromCollection?: (klydoId: string) => void;
  handleDeleteKlydo?: (klydoId: string) => void | undefined;
  cardNumber?: number;
  isDragging?: boolean;
};

const KlydoCard = ({
  klydo,
  isStatic,
  isMenuHidden,
  isSelected,
  isDraggedHovered,
  showDragIconOnHover,
  onSelect,
  handleRemoveKlydoFromCollection,
  handleDeleteKlydo,
  pendulumLogo,
  pendulumLogoSize,
  isApprovedCollection,
  pendulumLogoType,
  cardNumber,
  isDragging,
}: KlydoCardProps) => {
  const { spacing, palette } = useTheme();
  const {
    menuButtonItemsList,
    profileIncompleteErrorDialogOpen,
    handleCloseErrorDialog,
    onEditProfile,
    onClosedSubmitDialog,
    submitDialogOpen,
    onApprovedSubmit,
    publicUnsupportedProperties,
    specialPropertiesDialogOpen,
    onConfirmSpecialProperties,
    onClosedSpecialPropertiesDialog,
    isEditAvailable,
    handleKlydoCardClick,
    klydoIncompleteErrorDialogOpen,
  } = useKlydoCard({
    klydo,
    handleDeleteKlydo,
    handleRemoveKlydoFromCollection,
  });

  const [isHovered, setIsHovered] = useState(false);

  const handleCardClick = () => {
    if (onSelect) {
      onSelect(klydo);
    } else {
      handleKlydoCardClick();
    }
  };

  const cardStatusStyle = {
    position: 'absolute',
    top: spacing(1),
    right: spacing(1),
    zIndex: 10,
    width: 23,
    height: 23,
    borderRadius: '50%',
    m: 1,
    backgroundColor: palette.common.white,
    color: palette.common.black,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const handleCardStatus = () => {
    if (
      showDragIconOnHover &&
      (isDraggedHovered || (isHovered && !isDragging))
    ) {
      return (
        <DSBox sx={{ ...cardStatusStyle, backgroundColor: 'none' }}>
          <DragIndicatorIcon
            sx={{
              fontSize: 20,
              color: palette.white[56],
            }}
          />
        </DSBox>
      );
    }

    if (isDragging && cardNumber !== undefined) {
      return (
        <DSBox sx={cardStatusStyle}>
          <DSTypography
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              lineHeight: 1,
              color: palette.common.black,
            }}
          >
            {cardNumber}
          </DSTypography>
        </DSBox>
      );
    }

    if (onSelect && (isHovered || isSelected)) {
      return (
        <DSBox
          sx={{
            position: 'absolute',
            top: spacing(1),
            right: spacing(1),
            zIndex: 10,
          }}
        >
          <DSRadio
            checked={!!isSelected}
            sx={{
              color: isSelected ? palette.common.white : palette.white[56],
              '& .MuiSvgIcon-root': {
                color: isSelected ? palette.common.white : palette.white[56],
              },
            }}
          />
        </DSBox>
      );
    }

    return null;
  };

  return (
    <DSBox
      sx={{ position: 'relative' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <DSKlydoCard
        onClick={handleCardClick}
        sx={{
          // Width is set explicitly, so DSKlydoCard's 42/30 ratio is opted out
          // of — it would derive a height from the wider card. `minHeight` puts
          // the height back where that ratio had it: 42/30 at spacing(42) =
          // 336px wide gave 240px = spacing(30), which is also DSKlydoCard's
          // maxHeight. Pinning it there restores the vertical breathing room the
          // ratio used to provide, whether the status shows one row or two.
          aspectRatio: 'auto',
          maxWidth: 380,
          minHeight: spacing(30),
          paddingY: spacing(1),
          position: 'relative',
          cursor: onSelect
            ? 'pointer'
            : isEditAvailable
              ? 'pointer'
              : 'default',
          border: isSelected
            ? `3px solid ${palette.common.white}`
            : '3px solid transparent',
          transition: 'all 0.2s',
          backgroundColor:
            isHovered && !isDragging && !isDraggedHovered
              ? palette.white[10]
              : 'transparent',
        }}
      >
        {handleCardStatus()}
        <KlydoPreview
          klydo={klydo}
          scale={0.2}
          isPaused={isStatic}
          dialsHidden
          pendulumLogo={pendulumLogo}
          pendulumLogoSize={pendulumLogoSize}
          isApprovedCollection={isApprovedCollection}
          pendulumLogoType={pendulumLogoType}
          backgroundHidden
        />

        <DSBox
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <DSCardContent
            sx={{
              textAlign: 'left',
              paddingBottom: 0,
              paddingX: 0,
            }}
          >
            <DSBox sx={{ minHeight: spacing(14), maxWidth: spacing(19) }}>
              <DSTitleTypography
                sx={{
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordWrap: 'break-word',
                  width: '100%',
                  whiteSpace: 'normal',
                }}
              >
                {klydo.name}
              </DSTitleTypography>

              <DSTypography variant="body1">
                Updated At{' '}
                {new Date(klydo.updatedAt)
                  .toLocaleDateString(undefined, {
                    year: '2-digit',
                    month: '2-digit',
                    day: '2-digit',
                  })
                  .replace(/\//g, '.')}
              </DSTypography>
            </DSBox>
          </DSCardContent>
          <DSDivider
            flexItem
            sx={{
              alignSelf: 'flex-start',
              width: '90%',
            }}
          />
          <DSBox sx={{ padding: 0 }}>
            <DSCardActions
              disableSpacing
              sx={{ justifyContent: 'space-between', paddingX: 0 }}
              onClick={(event: MouseEvent<HTMLDivElement>) =>
                event.stopPropagation()
              }
            >
              <DSBox sx={{ minWidth: STATUS_MIN_WIDTH }}>
                <KlydoStatus klydo={klydo} />
              </DSBox>
              {!isMenuHidden && <MenuButton menuItems={menuButtonItemsList} />}
            </DSCardActions>
          </DSBox>
        </DSBox>
        <ErrorDialog
          errorTitle={INCOMPLETE_KLYDO_DATA_ALERT}
          errorReason={INCOMPLETE_KLYDO_DATA_REASON_ALERT}
          isErrorDialogOpen={klydoIncompleteErrorDialogOpen}
          handleClose={handleCloseErrorDialog}
          onClick={() => handleKlydoCardClick()}
        />
        <ErrorDialog
          errorTitle={INCOMPLETE_PROFILE_ALERT}
          errorReason={INCOMPLETE_PROFILE_REASON_ALERT}
          isErrorDialogOpen={profileIncompleteErrorDialogOpen}
          handleClose={handleCloseErrorDialog}
          onClick={onEditProfile}
        />
        <SpecialPropertiesModal
          open={specialPropertiesDialogOpen}
          properties={publicUnsupportedProperties}
          onClose={onClosedSpecialPropertiesDialog}
          onConfirm={onConfirmSpecialProperties}
        />
        <WarningDialog
          warningMessage={SUBMIT_FOR_REVIEW_ALERT}
          open={submitDialogOpen}
          handleConfirmationClick={onApprovedSubmit}
          handleClose={onClosedSubmitDialog}
        />
      </DSKlydoCard>
    </DSBox>
  );
};

export default KlydoCard;
